import L from 'leaflet';
import { localStorageKeys } from './constants';
import { buildZipFeatureIndex, getFeatureCoordinates } from '../pages/ZipcodeManaging/zipcodeMapUtils';

const CACHE_VERSION = 1;

/**
 * @typedef {Object} ZipcodeMapCacheEntry
 * @property {number} version
 * @property {number} fetchedAt
 * @property {'zip'|'branch'} searchMode
 * @property {string} zipInput
 * @property {string} branchInput
 * @property {string} zipcodesParam
 * @property {object} geoJson FeatureCollection
 * @property {{ zip: string, coords: [number, number] }[]} results
 * @property {string[]} notFound
 * @property {string|null} resolvedBy
 */

export function saveZipcodeMapCache(entry) {
  try {
    const payload = {
      version: CACHE_VERSION,
      fetchedAt: entry.fetchedAt ?? Date.now(),
      searchMode: entry.searchMode,
      zipInput: entry.zipInput ?? '',
      branchInput: entry.branchInput ?? '',
      zipcodesParam: entry.zipcodesParam ?? '',
      geoJson: entry.geoJson,
      results: (entry.results ?? []).map(({ zip, coords }) => ({
        zip,
        coords,
      })),
      notFound: entry.notFound ?? [],
      resolvedBy: entry.resolvedBy ?? null,
    };
    localStorage.setItem(localStorageKeys.ZIPCODE_MAP_CACHE, JSON.stringify(payload));
    return true;
  } catch (e) {
    console.warn('saveZipcodeMapCache failed', e);
    return false;
  }
}

/** @returns {ZipcodeMapCacheEntry|null} */
export function loadZipcodeMapCache() {
  try {
    const raw = localStorage.getItem(localStorageKeys.ZIPCODE_MAP_CACHE);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CACHE_VERSION || !parsed?.geoJson?.features?.length) {
      return null;
    }
    return parsed;
  } catch (e) {
    console.warn('loadZipcodeMapCache failed', e);
    return null;
  }
}

export function clearZipcodeMapCache() {
  try {
    localStorage.removeItem(localStorageKeys.ZIPCODE_MAP_CACHE);
  } catch (e) {
    console.warn('clearZipcodeMapCache failed', e);
  }
}

/** Rebuilds map rows with feature refs from cached GeoJSON. */
export function hydrateResultsFromCache(cacheEntry) {
  const index = buildZipFeatureIndex(cacheEntry.geoJson);
  const rows = [];

  for (const row of cacheEntry.results ?? []) {
    const zip = String(row?.zip ?? '').trim();
    if (!zip) continue;
    const feature = index.get(zip);
    const coords = row.coords ?? (feature ? getFeatureCoordinates(feature) : null);
    if (!coords) continue;
    rows.push({ zip, coords, feature });
  }

  return rows;
}

/** LatLngBounds for cached FeatureCollection (no API). */
export function getGeoJsonLatLngBounds(geoJson) {
  if (!geoJson?.features?.length) return null;
  try {
    const bounds = L.geoJSON(geoJson).getBounds();
    return bounds.isValid() ? bounds : null;
  } catch {
    return null;
  }
}

export function applyCacheToMapState(cacheEntry) {
  const results = hydrateResultsFromCache(cacheEntry);
  if (!results.length) return null;

  return {
    geoJson: cacheEntry.geoJson,
    results,
    notFound: cacheEntry.notFound ?? [],
    resolvedBy: cacheEntry.resolvedBy ?? null,
    searchMode: cacheEntry.searchMode === 'branch' ? 'branch' : 'zip',
    zipInput: cacheEntry.zipInput ?? '',
    branchInput: cacheEntry.branchInput ?? '',
    zipcodesParam: cacheEntry.zipcodesParam ?? '',
    fetchedAt: cacheEntry.fetchedAt ?? null,
  };
}
